import isString from 'lodash/isString';

import ParamTypesService from './BlockEditorParams/ParamTypes/ParamTypesService';
import ActionsMenuForEditNodeDataProvider from './IntegrationEditor/ActionsMenuForEditNodeDataProvider';
import ProfileSelection from './IntegrationEditor/ProfileSelection';
import { PARAM_INBOX, PARAM_RECIPIENT } from '@/components/BlockEditorNotification/NotificationSettings.vue';
import { EMPTY_STATE_OF_RICH_TEXT } from '@/components/common/RichTextBox/format-conversion-service.const';
import RichTextDataInputsService from '@/components/common/RichTextBox/rich-text-data-inputs-service';
import UserMatchingService from '@/utils/UserMatchingService';
import ElementService from '@/utils/element-service';
import { isNullOrUndefinedOrEmpty } from '@/utils/helpers';
import * as WorkflowNodeService from '@/utils/workflow-node-service';
import { TEST_RESULT_STATUSES } from '@/utils/workflow-node.const';

/* Private */

export const VALID_TO_PUBLISH_STATUSES = [TEST_RESULT_STATUSES.SUCCESS, TEST_RESULT_STATUSES.SKIPPED];

/* Public */

export const getStepDropdownMenuItems = (isSelectionForContextualData = false) => [
	{
		id: 'edit_step_setup',
		text: 'Edit step setup',
		isProfileSelection: true,
		component: ProfileSelection,
		props: {
			noBackButton: true,
			isSelectionForContextualData,
		},
	},
	{
		id: 'change_action',
		class: 'pt-5',
		text: 'Change action',
		component: ActionsMenuForEditNodeDataProvider,
		changeAction: true,
		props: {
			noBackButton: true,
			title: 'Choose action',
		},
	},
];

export const isStatusPublishValid = (status) => VALID_TO_PUBLISH_STATUSES.includes(status);

const allRequiredFieldsFilled = (
	actionHasDynamicParams,
	dynamicParamsOfCurrentNode,
	waitingForDynamicFields,
	editNodeParams,
	actionParamsInput,
	isWebhook,
	element,
	elementContext,
	isNativeElement,
	isSendNotification,
	notification,
	relevantEditNode,
	getConfigurationAndInterfaceIdByFieldUUID,
	getConfigurationFieldResponse,
	isConfigurationsOn
) => {
	const actionParams =
		actionHasDynamicParams && dynamicParamsOfCurrentNode && !waitingForDynamicFields
			? editNodeParams // the total fields of the node - including constant and the dynamic fields
			: actionParamsInput; // only the original fields of the node

	const requiredFields = actionParams.filter((p) => p.required);
	const paramsMap = isWebhook
		? ElementService.getElementFullParamsMap(element, elementContext, isNativeElement)
		: isSendNotification
		? notification
		: relevantEditNode?.params_map;

	let extendedParamsMap = paramsMap;
	if (relevantEditNode.user_schema_mappings_source_field_uuid) {
		extendedParamsMap = WorkflowNodeService.extendParamsMapWithParamsFromDynamicMapperIfExists(
			relevantEditNode,
			getConfigurationAndInterfaceIdByFieldUUID,
			getConfigurationFieldResponse,
			isConfigurationsOn,
			paramsMap
		);
	}

	if (requiredFields.length > 0 && !relevantEditNode?.hasOwnProperty('params_map')) return false;
	return requiredFields.every((p) => {
		if (!extendedParamsMap.hasOwnProperty(p.key)) {
			return false;
		}

		if (isSendNotification) {
			if (p.key === PARAM_INBOX && !notification?.is_inbox_mode) {
				return true;
			} else if (p.key === PARAM_RECIPIENT && notification?.is_inbox_mode) {
				return true;
			}
		}

		if (p.type === 'list') {
			// Special handling to check if a required multi item params are filled
			return p.params
				.filter((pa) => pa.required)
				.every((pa) => {
					if (!extendedParamsMap[p.key].hasOwnProperty(pa.key)) {
						return false;
					}
					const value = extendedParamsMap[p.key][pa.key];
					return !isNullOrUndefinedOrEmpty(value);
				});
		}

		if (p.type === 'item') {
			// Special handling to check if a required multi item params are filled
			return p.params
				.filter((pa) => pa.required)
				.every((pa) => {
					if (!extendedParamsMap[p.key].hasOwnProperty(pa.key)) {
						return false;
					}
					const value = extendedParamsMap[p.key][pa.key];
					return !isNullOrUndefinedOrEmpty(value);
				});
		}

		const value = extendedParamsMap[p.key];
		if (!isNullOrUndefinedOrEmpty(value)) {
			if (RichTextDataInputsService.isRichText(ParamTypesService.getTypeFormat(p.type, p.format))) {
				return value !== EMPTY_STATE_OF_RICH_TEXT;
			}
			if (isString(value) && value.includes(UserMatchingService.USER_MATCHING_OPTION_TEXT)) {
				// TODO - should be fixed to use only one key in order to check rather or not we have valid fallback
				const fallback =
					relevantEditNode?.fallbacks?.[p.key] || // this fallback comes from editing the editNode
					(relevantEditNode?.user_matching_fallbacks || []).find(({ param_key: paramKey }) => paramKey === p.key); // this fallback comes from server
				return UserMatchingService.validFallback(fallback);
			}
			return true;
		}
		return false;
	});
};

const BlockEditorService = {
	getStepDropdownMenuItems,
	isStatusPublishValid,
	allRequiredFieldsFilled,
};

export default BlockEditorService;
